import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { HERO_LINES, STATS } from "../data/content";
import { scrollToId } from "../lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero"
      className="relative flex min-h-[100svh] flex-col overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-24">
        <img
          src="/images/unsplash-ocean.jpg"
          alt="Deep sea"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-ink/35" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-ink/25 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/10 to-ink/35" />
      </motion.div>
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg, rgba(148,163,184,0.05) 0 1px, transparent 1px 140px)",
        }}
      />

      <motion.img
        src="/images/logo-3d.png"
        alt="Udat Marine emblem"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 0.95, scale: 1 }}
        transition={{ duration: 1.4, delay: 1, ease: EASE }}
        className="animate-float-slow pointer-events-none absolute right-14 top-[22%] z-10 hidden w-72 drop-shadow-[0_40px_80px_rgba(217,35,35,0.14)] lg:block xl:w-80"
      />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto flex w-full max-w-[1400px] flex-1 flex-col justify-center px-6 pb-14 pt-32 lg:px-12"
      >
        <motion.p
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-steel"
        >
          <span className="inline-block h-2 w-2 rotate-45 bg-brand" />
          Udat Marine Sdn Bhd — Pengerang · Johor · Est. 2001
        </motion.p>

        <h1 className="mt-7 font-display text-[clamp(2.7rem,8.6vw,8.6rem)] font-medium uppercase leading-[0.92] tracking-tighter">
          {HERO_LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.accent ? "text-brand" : ""}`}
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.95, delay: 0.3 + i * 0.15, ease: EASE }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-12 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="max-w-md text-base font-light leading-relaxed text-steel sm:text-lg"
          >
            Shipping agency, salvage &amp; heavy lifting, fresh water, provisions and
            round-the-clock port support — keeping vessels moving across East &amp;
            West Johor since 2001.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-support"
              onClick={() => scrollToId("#contact")}
              className="group relative overflow-hidden bg-brand px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white"
            >
              <span className="relative z-10 transition-colors duration-300 group-hover:text-ink">
                Request Port Support
              </span>
              <span className="absolute inset-0 -translate-x-full bg-white transition-transform duration-500 ease-out group-hover:translate-x-0" />
            </button>
            <a
              data-testid="hero-cta-hotline"
              href="tel:+60177529304"
              className="border border-line px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-slate-100"
            >
              24/7 Hotline · +6017-7529304
            </a>
          </motion.div>
        </div>
      </motion.div>

      <div className="relative z-10 flex justify-end px-6 pb-6 lg:px-12">
        <div className="hidden items-end gap-3 lg:flex">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-steel [writing-mode:vertical-rl]">
            Scroll
          </span>
          <span className="block h-12 w-px overflow-hidden bg-line">
            <span className="animate-scroll-line block h-full w-full bg-brand" />
          </span>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.2 }}
        className="relative z-10 border-t border-line bg-ink/50 backdrop-blur-sm"
        data-testid="hero-stats"
      >
        <div className="mx-auto grid max-w-[1400px] grid-cols-2 divide-x divide-line px-6 lg:grid-cols-4 lg:px-12">
          {STATS.map((s) => (
            <div key={s.label} className="py-5 pl-4 lg:pl-8 first:pl-0">
              <div className="font-display text-xl text-slate-100 lg:text-2xl">
                {s.value}
              </div>
              <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-steel lg:text-[10px]">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
